import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { isNil } from 'lodash'
import pluralize from 'pluralize'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { FormField } from '@/components/forms/FormField'
import Select from '@/components/forms/Select'
import { Pill } from '@/components/pills/Pill'
import { sendSupportAction } from '@/reducers/organizations/organizationUsers.actions'

import { NON_DISRUPTIVE_SUPPORT_ACTIONS, SUPPORT_ACTION_OPTIONS } from './supportActions.constants'

/**
 * Lets Balto staff send a support action (force logout, pull logs, start/stop a call, reload)
 * to the desktop apps of one or more users.
 *
 * @param {object} props
 * @param {object[]} props.users Table rows for the users the action is sent to
 * @param {Function} props.onClose Called when the modal is dismissed or the action is sent
 */
export const SupportActionsModal = ({ users, onClose }) => {
  const dispatch = useDispatch()
  const [action, setAction] = useState(null)
  const [acknowledged, setAcknowledged] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const requiresAcknowledgement = !isNil(action) && !NON_DISRUPTIVE_SUPPORT_ACTIONS.includes(action)
  const canSubmit = !isNil(action) && (!requiresAcknowledgement || acknowledged)

  const handleActionChange = (option) => {
    setAction(option.value)
    // Acknowledging one disruptive action shouldn't carry over to a different one
    setAcknowledged(false)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    const sent = await dispatch(
      sendSupportAction({ usernames: users.map((user) => user.username), action })
    )

    if (sent) {
      onClose()
    } else {
      setSubmitting(false)
    }
  }

  return (
    <BasicModal
      data-testid="support-actions-modal"
      title="Support Actions"
      onClose={onClose}
      show
      size="small"
      scrolling
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            brand
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmit}
            data-testid="support-actions-submit"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="field-stack">
        <FormField label={pluralize('User', users.length, true)}>
          <div className="flex-wrap small-gap" data-testid="support-actions-users">
            {users.map((user) => (
              <Pill key={user.id}>{`${user.full_name} (${user.username})`}</Pill>
            ))}
          </div>
        </FormField>
        <FormField label="Action" required>
          <Select
            options={SUPPORT_ACTION_OPTIONS}
            value={action}
            onChange={handleActionChange}
            placeholder="Select an action..."
            isClearable={false}
          />
        </FormField>
        {requiresAcknowledgement && (
          <Checkbox
            label="I acknowledge this may disrupt anyone who is on an active call"
            checked={acknowledged}
            onChange={(event, data) => setAcknowledged(data.checked)}
            data-testid="support-actions-acknowledge"
          />
        )}
      </div>
    </BasicModal>
  )
}
